import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, AlertTriangle, Shield, Heart, Wallet, Clock, Car } from 'lucide-react';
import { FadeIn } from '@/components/Animations';

const RIDER_IMAGE = "https://customer-assets.emergentagent.com/job_csi-partnership-site/artifacts/4dsxhim1_pexels-chrisgrantharvey-33636676.jpg";

const problems = [
  {
    icon: Shield,
    title: 'Safety Risks',
    description: 'Riders face dangerous road conditions daily without proper training, equipment, or insurance coverage.',
  },
  {
    icon: Heart,
    title: 'No Benefits',
    description: 'As independent contractors, riders have no access to medical aid, pension, or any employment benefits.',
  },
  {
    icon: Wallet,
    title: 'Income Instability',
    description: 'Unpredictable earnings, no minimum wage guarantee, and no financial safety net during slow periods.',
  },
  {
    icon: Clock,
    title: 'Long Hours',
    description: 'Many riders work 12+ hour days, 7 days a week, just to make ends meet, with no overtime protection.',
  },
];

const ProblemPage = () => {
  return (
    <div className="min-h-screen pt-20" data-testid="problem-page">
      {/* Hero */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-rose-50 via-slate-50 to-white">
        <div className="container-wide">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <FadeIn>
              <div className="space-y-6">
                <span className="inline-block px-4 py-2 bg-rose-100 text-rose-600 rounded-full text-sm font-semibold tracking-wide uppercase">
                  The Problem
                </span>
                <h1 className="text-4xl md:text-6xl font-heading font-bold text-slate-900">
                  The Hidden Crisis on Our Roads
                </h1>
                <p className="text-xl text-slate-600 leading-relaxed">
                  Every day, thousands of delivery riders risk their lives to bring us convenience. 
                  Yet they work without safety nets, structured support, or a path to stability.
                </p>
              </div>
            </FadeIn>

            <FadeIn delay={0.2} direction="left">
              <div className="relative">
                <img
                  src={RIDER_IMAGE}
                  alt="Delivery Rider"
                  className="rounded-3xl shadow-xl w-full object-cover"
                />
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Problems Grid */}
      <section className="py-20 md:py-32 bg-white">
        <div className="container-wide">
          <FadeIn>
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-4xl md:text-5xl font-heading font-bold text-slate-900 mb-6">
                The Reality of Gig Work
              </h2>
              <p className="text-lg text-slate-600">
                The gig economy has created opportunities, but it has also created a vulnerable 
                workforce without the protections that traditional employment provides.
              </p>
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-8">
            {problems.map((problem, index) => (
              <FadeIn key={problem.title} delay={0.1 * index}>
                <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 hover:shadow-lg transition-all duration-300">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 bg-rose-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <problem.icon className="w-7 h-7 text-rose-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-heading font-semibold text-slate-900 mb-3">
                        {problem.title}
                      </h3>
                      <p className="text-slate-600">{problem.description}</p>
                    </div>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* The Human Cost */}
      <section className="py-20 md:py-32 bg-slate-900">
        <div className="container-wide">
          <FadeIn>
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-4xl md:text-5xl font-heading font-bold text-white mb-8">
                The Human Cost
              </h2>
              <p className="text-xl text-slate-300 leading-relaxed">
                Behind every delivery is a person with a family, dreams, and a life that matters. 
                These riders are the backbone of the modern economy, yet they remain invisible 
                and unprotected.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Why It Matters */}
      <section className="py-20 md:py-32 bg-white">
        <div className="container-wide">
          <FadeIn>
            <div className="bg-gradient-to-br from-purple-50 to-slate-50 rounded-3xl p-8 md:p-16">
              <div className="max-w-3xl mx-auto text-center">
                <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mb-6">
                  This Isn't Just About Riders
                </h2>
                <p className="text-lg text-slate-600 leading-relaxed mb-8">
                  The gig economy affects all of us. When riders are unsafe, our communities are 
                  unsafe. When riders struggle, families struggle. When we ignore the problem, 
                  we become part of it.
                </p>
                <p className="text-lg text-slate-600 leading-relaxed mb-8">
                  <strong>#MyLifeMatters</strong> exists because we believe in a better way — 
                  a new social compact that provides structure, safety, and opportunity for 
                  every rider.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <Button
                    asChild
                    className="bg-primary-mlm hover:bg-[#5b21b6] text-white rounded-full px-8 py-6 font-semibold"
                    data-testid="see-solution-btn"
                  >
                    <Link to="/solution">
                      See Our Solution
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Link>
                  </Button>
                  <Button
                    asChild
                    variant="outline"
                    className="border-2 border-primary-mlm text-primary-mlm hover:bg-primary-mlm hover:text-white rounded-full px-8 py-6 font-semibold"
                    data-testid="join-movement-btn"
                  >
                    <Link to="/corporate-csi">
                      Join The Movement
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

export default ProblemPage;
